<template>
  <div
    v-if="state"
    class="ais-GeoSearch"
  >
    <googlemaps-map
      ref="map"
      :center.sync="center"
      :zoom="13"
      :options="mapOptions"
      @click="handleMapClick"
      @center_changed="onCenterChanged"
      @idle="onIdle"
      :class="{
        'ais-GeoSearch-map': true,
        dragging: isMinimized || isDragging,
      }"
    >
      <map-fit-bounds>
        <googlemaps-marker
          v-for="(i, index) of state.items"
          :key="i.objectID"
          :ref="'marker' + i.objectID"
          :title="i.city"
          :icon="icon(i)"
          :position="i._geoloc"
          @click="handleMarkerClick(i, index)"
          :zIndex="icon(i).zIndex"
        >
          <map-info-window v-if="infoWinOpen && item === i">
            <ResultItem
              v-if="i"
              :item="i"
              :insights="wrapInsights(i, itemIndex)"
              :store="store"
              class="item item-map"
              type="Map"
              @modalInterface="modalInterface"
            />
          </map-info-window>
        </googlemaps-marker>
      </map-fit-bounds>
    </googlemaps-map>

    <div
      class="carousel-container"
      :class="{ minimized: isMinimized || isDragging }"
    >
      <div class="d-flex mapHandle">
        <CustomStats class="d-flex-grow" />
        <button
          @click="isMinimized = !isMinimized"
          class="invisible-button"
        >
          <template v-if="isMinimized">
            <up-icon />
          </template>
          <template v-else>
            <up-icon />
          </template>
        </button>
      </div>

      <Carousel3d
        ref="carousel"
        v-show="!isMinimized"
        :width="250"
        :height="320"
        :display="5"
        :space="280"
        :perspective="10"
        :inverse-scaling="0"
        @before-slide-change="beforeSlideChange"
      >
        <Slide
          v-for="(item, index) in state.items"
          :index="index"
          :key="item.objectID"
        >
          <ResultItem
            class="item item-grid"
            :item="item"
            :insights="wrapInsights(item, index)"
            type="Map"
            :store="store"
            @click.native="setCurrentItem(item, index)"
            @modalInterface="modalInterface"
          />
        </Slide>
      </Carousel3d>
    </div>
  </div>
</template>

<script>
import { createWidgetMixin } from 'vue-instantsearch';
import { connectGeoSearch } from 'instantsearch.js/es/connectors';
import { Carousel3d, Slide } from 'vue-carousel-3d';
import ResultItem from './ResultItem.vue';
import CustomStats from './CustomStats.vue';
import MapInfoWindow from './MapInfoWindow';
import MapFitBounds from './MapFitBounds';
import UpIcon from '@/assets/icons/chevron-up.svg';

export default {
  mixins: [createWidgetMixin({ connector: connectGeoSearch })],
  props: {
    store: {
      type: Object,
      required: true,
    },
  },
  components: {
    ResultItem,
    CustomStats,
    Carousel3d,
    Slide,
    UpIcon,
    MapInfoWindow,
    MapFitBounds,
  },
  data() {
    return {
      carouselVisible: false,
      hover: false,
      mapOptions: {
        disableDefaultUI: true,
        gestureHandling: 'cooperative',
        zoomControl: true,
      },
      item: null,
      infoWindowPos: null,
      infoWinOpen: false,
      isDragging: false,
      isMinimized: false,
      currentOpenItem: null,
      resizeListener: null,
    };
  },
  mounted() {
    const mq = window.matchMedia('screen and (max-width: 769px)');
    this.carouselVisible = mq.matches;
    mq.addListener(e => {
      this.carouselVisible = e.matches;
    });

    this.resizeListener = function() {
      // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
      let vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }.bind(this);
    window.addEventListener('resize', this.resizeListener);
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeListener);
  },
  computed: {
    center() {
      if (this.item == null) {
        return this.state.items[0]._geoloc;
      } else {
        return this.item._geoloc;
      }
    },
  },
  methods: {
    wrapInsights(item, position) {
      return (method, payload) =>
        this.instantSearchInstance.insightsClient(method, {
          positions: [position],
          index: this.instantSearchInstance.indexName,
          queryID: this.instantSearchInstance.helper.lastResults.queryID,
          ...payload,
        });
    },
    itemActive(item) {
      const somethingActive = this.carouselVisible ? true : this.infoWinOpen;
      return (
        somethingActive && this.item && item.objectID == this.item.objectID
      );
    },
    iconAsset(item) {
      if (item.type === 'cinema') {
        return require('../assets/pin-cinema.png?external');
      } else {
        if (this.itemActive(item)) {
          return require('../assets/pin-restaurant-pink-small.png');
        } else {
          return require('../assets/pin-restaurant-black.png');
        }
      }
    },
    icon(item) {
      return {
        optimized: true,
        url: this.iconAsset(item),
        scaledSize: this.itemActive(item)
          ? { width: 30, height: 42, f: 'px', b: 'px' }
          : { width: 24, height: 34, f: 'px', b: 'px' },
        zIndex: this.itemActive(item) ? 999 : 1,
      };
    },
    onCenterChanged(e) {
      this.$emit('update:aroundLatLng', `${e.lat()},${e.lng()}`);
      this.isDragging = true;
    },
    onIdle() {
      this.isDragging = false;
    },
    handleMapClick() {
      if (this.infoWinOpen) {
        this.infoWinOpen = false;
      }
    },
    handleInfoWindowClose() {
      this.infoWinOpen = false;
    },
    beforeSlideChange(newIndex) {
      if (newIndex) {
        this.setCurrentItem(this.state.items[newIndex], newIndex);
      }
    },
    handleMarkerClick(item, index) {
      // console.log('marker click'); // ok
      setTimeout(() => {
        this.setCurrentItem(item, index);
        this.isMinimized = false;
        if (this.carouselVisible) {
          this.$refs.carousel.goSlide(index);
        }
      }, 10);
    },
    setCurrentItem(item, index) {
      this.item = item;
      this.itemIndex = index;
      this.isDragging = false;

      if (!this.carouselVisible) {
        this.toggleInfoWindow(item);
      }
    },
    toggleInfoWindow(item) {
      this.infoWindowPos = item._geoloc;
      // check if its the same item that was selected if yes toggle
      if (this.currentOpenItem === item.objectID) {
        this.infoWinOpen = !this.infoWinOpen;
      }
      // if different item set infowindow to open and reset current item index
      else {
        this.infoWinOpen = true;
        this.currentOpenItem = item.objectID;
      }
    },
    modalInterface(modalType) {
      // console.log('reemition back to parent component Results');
      // console.log('passed modalType: ' + modalType);
      this.$emit('modalInterface',modalType);
    },
  },
};
</script>

<style lang="scss" scoped>
.vue-map-container,
.vue-map-container .vue-map {
  width: 100%;
  height: 100%;
}

/deep/ .map-view {
  width: 100%;
  height: 100%;
}

/deep/ object {
  height: 0;
}

.ais-GeoSearch {
  // TODO: instead this should have height: 100% as well as all its parents
  height: calc(100vh - 240px);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.ais-GeoSearch-map {
  flex: 100% 1 1;
}
.description-container {
  background-color: white;
}

// Mobile View: Present map *and* carousel
@media screen and (max-width: $tablet) {
  .ais-Stats {
    text-align: center;
  }
  .ais-GeoSearch {
    height: calc(100vh - 94px); // 94px is the height of fixed header.
    height: calc(var(--vh, 1vh) * 100 - 94px);
  }
  .ais-GeoSearch-map {
    flex-basis: 100%;
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 200px; // maybe https://stackoverflow.com/a/46522358
  }
  .carousel-container {
    flex-grow: 0;
    flex-shrink: 0;
    padding: 0;
    box-shadow: -2px 1px 5px 5px rgba(0, 0, 0, 0.2);
    z-index: 10;
  }
}

// Desktop View: Present *only* map
@media screen and (min-width: $tablet + 1) {
  .carousel-container {
    display: none;
  }
}

.mapHandle {
  margin: 1em 0;
}

.d-flex {
  display: flex;
}

.d-flex-grow {
  flex-grow: 1;
}

.invisible-button {
  border: none;
  font: inherit;
  background: none;
}

.minimized .invisible-button svg {
  transform: rotateX(0);
}

.invisible-button svg {
  width: 2em;
  transform: rotateX(180deg);
}

/deep/ {
  .gm {
    &-style {
      #{&} &-iw {
        &-d {
          overflow: auto !important;
          min-width: 100%;
        }
        &-c {
          padding: 0;
          border-radius: 0;
          // set tooltip width for desktop
          @media screen and (min-width: $tablet + 1) {
            width: 500px;
          }
        }
        &-t {
          &::after {
            display: none;
          }
        }
      }
    }
    &-ui {
      &-hover {
        &-effect {
          display: none;
        }
      }
    }
  }
}

/deep/ {
  .carousel-3d-slide {
    background-color: transparent;
    border-width: 0 !important;
    padding: 0 4px 4px 4px;
  }

  .carousel-3d-container {
    margin: auto !important;
  }

  .item.item-grid {
    height: 315px;
    border: 1px solid rgba(0, 0, 0, 0.25);
  }
}
</style>
